import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Input, Spin } from "antd";

import config from "../../config";
import "./submit2fa.css";

/**
 * The page an office contact lands on from the 2FA email/SMS.
 *
 * Standalone by design: no DashboardLayout, no sidebar, no auth check. The code
 * arrives on someone's phone, and requiring them to find a laptop and sign in is
 * what loses the five-minute window.
 *
 * It shows only what the server gives it — carrier, practice and a countdown.
 * There is deliberately no patient name here, and no request that could fetch
 * one: a link sent by SMS must be assumed readable by whoever holds the phone.
 *
 * Every server failure (unknown / expired / used / job no longer waiting) comes
 * back as the same 404, so this renders one honest "link is no longer valid"
 * state rather than guessing which it was.
 */

function formatCountdown(sec) {
  if (sec == null) return "";
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m + ":" + (s < 10 ? "0" + s : s);
}

export default function Submit2FAPage(props) {
  const token =
    (props.match && props.match.params && props.match.params.token) || "";

  const [status, setStatus] = useState("loading"); // loading|ready|invalid|done
  const [info, setInfo] = useState(null);
  const [seconds, setSeconds] = useState(null);
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const tick = useRef(null);

  const url = `${config.apiGateway.URL}/clinics/twofa/${encodeURIComponent(token)}/`;

  const load = useCallback(
    async function loadInner() {
      try {
        const res = await fetch(url, { method: "GET" });
        if (!res.ok) {
          setStatus("invalid");
          return;
        }
        const body = await res.json();
        setInfo(body);
        setSeconds(body.seconds_remaining);
        setStatus("ready");
      } catch (e) {
        setStatus("invalid");
      }
    },
    [url],
  );

  useEffect(
    function () {
      if (!token) {
        setStatus("invalid");
        return undefined;
      }
      load();
      return undefined;
    },
    [token, load],
  );

  // Local countdown. The server is the authority on expiry — this only stops the
  // user typing into a window that has already closed.
  useEffect(
    function () {
      if (status !== "ready") return undefined;
      tick.current = setInterval(function () {
        setSeconds(function (s) {
          if (s == null) return s;
          return s > 0 ? s - 1 : 0;
        });
      }, 1000);
      return function () {
        clearInterval(tick.current);
        tick.current = null;
      };
    },
    [status],
  );

  async function handleSubmit() {
    const value = (code || "").trim();
    if (!value) {
      setError("Enter the code the carrier sent.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: value }),
      });
      if (res.ok) {
        setStatus("done");
      } else if (res.status === 404) {
        // Covers expired, already-used and "the run moved on" alike — the server
        // does not distinguish them, and neither should this.
        setStatus("invalid");
      } else {
        const body = await res.json().catch(function () { return {}; });
        setError(body.detail || "That didn't go through. Try again.");
      }
    } catch (e) {
      setError("That didn't go through. Try again.");
    } finally {
      setSubmitting(false);
    }
  }

  const expired = seconds != null && seconds <= 0;

  function body() {
    if (status === "loading") {
      return (
        <div className="tfa-center">
          <Spin />
        </div>
      );
    }

    if (status === "invalid") {
      return (
        <>
          <h1 className="tfa-title">This link is no longer valid</h1>
          <p className="tfa-body">
            Verification links work for a few minutes only, and once a code has
            been submitted the link closes. Ask whoever started the check in
            Smilepass to run it again.
          </p>
        </>
      );
    }

    if (status === "done") {
      return (
        <>
          <h1 className="tfa-title">Thanks — we've got it</h1>
          <p className="tfa-body">
            The code is on its way to {(info && info.carrier) || "the carrier"}.
            You can close this page.
          </p>
        </>
      );
    }

    return (
      <>
        <h1 className="tfa-title">Verification code needed</h1>
        <p className="tfa-meta">
          {[(info && info.carrier) || null, (info && info.office_name) || null]
            .filter(Boolean)
            .join(" · ")}
        </p>
        <p className="tfa-body">
          {(info && info.carrier) || "The insurance portal"} sent a verification
          code to your practice. Enter it below to keep the check going.
        </p>

        <Input
          autoFocus
          size="large"
          className="tfa-input"
          value={code}
          placeholder="Code"
          disabled={expired || submitting}
          // A numeric keypad on a phone, where this is nearly always opened.
          inputMode="numeric"
          onChange={function (e) { setCode(e.target.value); }}
          onPressEnter={handleSubmit}
        />

        {error ? <p className="tfa-error">{error}</p> : null}

        <Button
          type="primary"
          size="large"
          block
          className="tfa-submit"
          loading={submitting}
          disabled={expired}
          onClick={handleSubmit}
        >
          Submit code
        </Button>

        <p className={"tfa-countdown" + (expired ? " is-expired" : "")}>
          {expired
            ? "This link has expired — the check needs to be started again."
            : "Time left  " + formatCountdown(seconds)}
        </p>
      </>
    );
  }

  return (
    <div className="tfa-page">
      <div className="tfa-card">
        <div className="tfa-brand">Smilepass</div>
        {body()}
      </div>
    </div>
  );
}
